import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { COLORS, TYPOGRAPHY, SPACING, RADIUS, SHADOWS } from '../designSystem';

const API_URL = process.env.REACT_APP_API_URL || 'http://127.0.0.1:8000';

// Template type options — must match Backend/constants.py TEMPLATE_TYPE_ENUM
const TEMPLATE_TYPES = [
  { value: 'main_contract',                label: 'Main Contract' },
  { value: 'state_disclosure',             label: 'State Disclosure' },
  { value: 'refi_third_party_payoff_page', label: 'Refi / Third Party Payoff' },
  { value: 'weekly_addendum',              label: 'Weekly Addendum' },
  { value: 'early_pay_addendum',           label: 'Early Pay Addendum' },
  { value: 'additional_entity_addendum',   label: 'Additional Entity Addendum' },
  { value: 'misc',                         label: 'Miscellaneous' },
];

export default function Phase7TemplateManagement({ fundingEntityId, token }) {
  // State
  const navigate = useNavigate();
  const [templates, setTemplates] = useState([]);
  const [versions, setVersions] = useState({}); // Map of templateId -> [versions]
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [createTemplateOpen, setCreateTemplateOpen] = useState(false);
  const [newTemplateType, setNewTemplateType] = useState('main_contract');
  const [creatingTemplate, setCreatingTemplate] = useState(false);
  const [creatingVersions, setCreatingVersions] = useState({}); // Map of templateId -> bool
  const [expandedTypes, setExpandedTypes] = useState({}); // Map of template type -> expanded bool
  const [showArchived, setShowArchived] = useState({}); // Map of template type -> show archived bool
  const [renamingVersion, setRenamingVersion] = useState(null); // { templateId, versionId, currentName }
  const [newVersionName, setNewVersionName] = useState('');
  const [updatingVersion, setUpdatingVersion] = useState(null);

  // Fetch versions for a specific template
  const fetchVersions = useCallback(async (templateId, versionsMap = null) => {
    try {
      const res = await axios.get(
        `${API_URL}/contract-templates/${templateId}/versions`,
        {
          headers: token ? { Authorization: `Bearer ${token}` } : {},
        }
      );
      const versionList = res.data || [];
      if (versionsMap) {
        versionsMap[templateId] = versionList;
      } else {
        setVersions(prev => ({
          ...prev,
          [templateId]: versionList,
        }));
      }
    } catch (err) {
      console.error(`Failed to fetch versions for template ${templateId}:`, err);
    }
  }, [token]);

  // Fetch templates for this funder
  const fetchTemplates = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const res = await axios.get(
        `${API_URL}/contract-templates/by-funder/${fundingEntityId}`,
        {
          headers: token ? { Authorization: `Bearer ${token}` } : {},
        }
      );
      const templateList = res.data || [];
      setTemplates(templateList);

      // Fetch versions for each template
      const versionsMap = {};
      for (const template of templateList) {
        await fetchVersions(template.id, versionsMap);
      }
      setVersions(versionsMap);
    } catch (err) {
      console.error('Failed to fetch templates:', err);
      setError('Failed to load templates');
    } finally {
      setLoading(false);
    }
  }, [fundingEntityId, token, fetchVersions]);

  // Create new template
  const handleCreateTemplate = async () => {
    // Template name is derived from template type
    const templateTypeObj = TEMPLATE_TYPES.find(t => t.value === newTemplateType);
    const templateName = templateTypeObj ? templateTypeObj.label : newTemplateType;

    setCreatingTemplate(true);
    try {
      // eslint-disable-next-line no-unused-vars
      const res = await axios.post(
        `${API_URL}/contract-templates`,
        {
          funding_entity_id: fundingEntityId,
          name: templateName,
          template_type: newTemplateType,
        },
        {
          headers: token ? { Authorization: `Bearer ${token}` } : {},
        }
      );

      // Reset modal
      setNewTemplateType('main_contract');
      setCreateTemplateOpen(false);
      setError(null);

      // Refresh templates
      await fetchTemplates();
    } catch (err) {
      console.error('Failed to create template:', err);
      setError(err.response?.data?.detail || 'Failed to create template');
    } finally {
      setCreatingTemplate(false);
    }
  };

  // Create new version for a template
  const handleCreateVersion = async (templateId) => {
    setCreatingVersions(prev => ({
      ...prev,
      [templateId]: true,
    }));

    try {
      // eslint-disable-next-line no-unused-vars
      const res = await axios.post(
        `${API_URL}/contract-templates/${templateId}/versions`,
        {},
        {
          headers: token ? { Authorization: `Bearer ${token}` } : {},
        }
      );

      // Refresh versions for this template
      await fetchVersions(templateId);
      setError(null);
    } catch (err) {
      console.error('Failed to create version:', err);
      setError(err.response?.data?.detail || 'Failed to create version');
    } finally {
      setCreatingVersions(prev => ({
        ...prev,
        [templateId]: false,
      }));
    }
  };

  // Open authoring editor for a version
  const handleOpenAuthoring = (templateId, versionId) => {
    navigate(`/template-authoring/${templateId}/${versionId}`);
  };

  // Start renaming a version
  const startRenaming = (templateId, versionId, currentName) => {
    setRenamingVersion({ templateId, versionId, currentName });
    setNewVersionName(currentName || '');
  };

  // Cancel renaming
  const cancelRenaming = () => {
    setRenamingVersion(null);
    setNewVersionName('');
  };

  // Save version name
  const handleSaveVersionName = async (templateId, versionId) => {
    setUpdatingVersion(versionId);
    try {
      const updatePayload = {
        version_name: newVersionName || null, // null means clear the name
      };
      await axios.patch(
        `${API_URL}/contract-templates/${templateId}/versions/${versionId}`,
        updatePayload,
        {
          headers: token ? { Authorization: `Bearer ${token}` } : {},
        }
      );

      // Refresh versions
      await fetchVersions(templateId);
      cancelRenaming();
      setError(null);
    } catch (err) {
      console.error('Failed to save version name:', err);
      setError(err.response?.data?.detail || 'Failed to save version name');
    } finally {
      setUpdatingVersion(null);
    }
  };

  // Archive a version
  const handleArchiveVersion = async (templateId, versionId) => {
    setUpdatingVersion(versionId);
    try {
      const updatePayload = {
        is_archived: true,
      };
      await axios.patch(
        `${API_URL}/contract-templates/${templateId}/versions/${versionId}`,
        updatePayload,
        {
          headers: token ? { Authorization: `Bearer ${token}` } : {},
        }
      );

      // Refresh versions
      await fetchVersions(templateId);
      setError(null);
    } catch (err) {
      console.error('Failed to archive version:', err);
      setError(err.response?.data?.detail || 'Failed to archive version');
    } finally {
      setUpdatingVersion(null);
    }
  };

  // Unarchive a version
  const handleUnarchiveVersion = async (templateId, versionId) => {
    setUpdatingVersion(versionId);
    try {
      const updatePayload = {
        is_archived: false,
      };
      await axios.patch(
        `${API_URL}/contract-templates/${templateId}/versions/${versionId}`,
        updatePayload,
        {
          headers: token ? { Authorization: `Bearer ${token}` } : {},
        }
      );

      // Refresh versions
      await fetchVersions(templateId);
      setError(null);
    } catch (err) {
      console.error('Failed to unarchive version:', err);
      setError(err.response?.data?.detail || 'Failed to unarchive version');
    } finally {
      setUpdatingVersion(null);
    }
  };

  // Toggle type expansion
  const toggleTypeExpanded = (templateType) => {
    setExpandedTypes(prev => ({
      ...prev,
      [templateType]: !prev[templateType],
    }));
  };

  // Toggle show archived for a type
  const toggleShowArchived = (templateType) => {
    setShowArchived(prev => ({
      ...prev,
      [templateType]: !prev[templateType],
    }));
  };

  // Group templates by type
  const templatesByType = {};
  templates.forEach(template => {
    const type = template.template_type || 'disclosure';
    if (!templatesByType[type]) {
      templatesByType[type] = [];
    }
    templatesByType[type].push(template);
  });

  // Get version display name
  const getVersionName = (version) => {
    if (version.version_name) {
      return version.version_name;
    }
    return `Version ${version.id}`;
  };

  // Format template type label using canonical user-friendly names
  const formatTypeLabel = (type) => {
    const found = TEMPLATE_TYPES.find(t => t.value === type);
    return found ? found.label : type.split('_').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ');
  };

  // Initial fetch
  useEffect(() => {
    fetchTemplates();
  }, [fetchTemplates]);

  return (
    <div style={styles.card}>
      {/* Card Header */}
      <div style={styles.cardHeader}>
        <h2 style={styles.cardTitle}>Contract Templates</h2>
        <button
          onClick={() => setCreateTemplateOpen(true)}
          style={styles.createButton}
        >
          + Create Template
        </button>
      </div>

      {/* Error Message */}
      {error && (
        <div style={styles.errorMessage}>
          {error}
        </div>
      )}

      {/* Loading State */}
      {loading && templates.length === 0 && (
        <div style={styles.emptyState}>
          <p style={TYPOGRAPHY.BODY}>Loading templates...</p>
        </div>
      )}

      {/* Empty State */}
      {!loading && templates.length === 0 && (
        <div style={styles.emptyState}>
          <p style={{ ...TYPOGRAPHY.BODY, color: COLORS.TEXT_LIGHT, marginBottom: SPACING.MD }}>
            No templates yet. Create your first template to get started.
          </p>
          <button
            onClick={() => setCreateTemplateOpen(true)}
            style={styles.secondaryButton}
          >
            Create First Template
          </button>
        </div>
      )}

      {/* Templates grouped by type */}
      {!loading && templates.length > 0 && (
        <div style={styles.templatesList}>
          {Object.entries(templatesByType).map(([ templateType, templatesOfType ]) => {
            const isExpanded = expandedTypes[templateType] || false;
            const showArchivedForType = showArchived[templateType] || false;

            return (
              <div key={templateType} style={styles.typeSection}>
                {/* Type Header - Collapsible */}
                <button
                  onClick={() => toggleTypeExpanded(templateType)}
                  style={{
                    ...styles.typeHeader,
                    backgroundColor: isExpanded ? COLORS.BG_SURFACE_SECONDARY : COLORS.BG_SURFACE_PRIMARY,
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: SPACING.MD, flex: 1 }}>
                    <span style={{ fontSize: '16px', transition: 'transform 0.2s ease', transform: isExpanded ? 'rotate(90deg)' : 'rotate(0deg)' }}>
                      ▶
                    </span>
                    <h3 style={styles.typeTitle}>
                      {formatTypeLabel(templateType)}
                    </h3>
                    <span style={styles.typeCount}>
                      ({templatesOfType.length})
                    </span>
                  </div>
                </button>

                {/* Templates of this type - shown when expanded */}
                {isExpanded && (
                  <div style={styles.typeContent}>
                    {templatesOfType.map((template, idx) => {
                      const templateVersions = versions[template.id] || [];
                      const activeVersions = templateVersions.filter(v => !v.is_archived);
                      const archivedVersions = templateVersions.filter(v => v.is_archived);

                      return (
                        <div key={template.id} style={{
                          ...styles.templateItem,
                          borderBottom: idx < templatesOfType.length - 1 ? `1px solid ${COLORS.BORDER_LIGHT}` : 'none',
                        }}>
                          {/* Active Versions List */}
                          {activeVersions.length > 0 && (
                            <div style={styles.versionsList}>
                              {activeVersions.map((version) => {
                                const isRenaming = renamingVersion?.versionId === version.id;
                                const isUpdating = updatingVersion === version.id;

                                return (
                                  <div key={version.id} style={styles.versionItem}>
                                    <div style={styles.versionInfo}>
                                      {isRenaming ? (
                                        <input
                                          type="text"
                                          value={newVersionName}
                                          onChange={(e) => setNewVersionName(e.target.value)}
                                          style={styles.versionNameInput}
                                          placeholder="Enter version name"
                                          autoFocus
                                          disabled={isUpdating}
                                        />
                                      ) : (
                                        <span
                                          onClick={() => startRenaming(template.id, version.id, version.version_name)}
                                          style={{
                                            ...styles.versionLabel,
                                            cursor: 'pointer',
                                            textDecoration: 'underline',
                                          }}
                                          title="Click to rename"
                                        >
                                          {getVersionName(version)}
                                        </span>
                                      )}
                                      {version.is_active && (
                                        <span style={styles.activeBadge}>Active</span>
                                      )}
                                    </div>
                                    <div style={styles.versionActions}>
                                      {isRenaming ? (
                                        <>
                                          <button
                                            onClick={() => handleSaveVersionName(template.id, version.id)}
                                            disabled={isUpdating}
                                            style={{ ...styles.editButton, opacity: isUpdating ? 0.6 : 1 }}
                                          >
                                            {isUpdating ? '...' : 'Save'}
                                          </button>
                                          <button
                                            onClick={cancelRenaming}
                                            disabled={isUpdating}
                                            style={{ ...styles.cancelButton, opacity: isUpdating ? 0.6 : 1 }}
                                          >
                                            Cancel
                                          </button>
                                        </>
                                      ) : (
                                        <>
                                          <button
                                            onClick={() => handleOpenAuthoring(template.id, version.id)}
                                            style={styles.editButton}
                                          >
                                            Edit
                                          </button>
                                          <button
                                            onClick={() => handleArchiveVersion(template.id, version.id)}
                                            disabled={isUpdating}
                                            style={{ ...styles.archiveButton, opacity: isUpdating ? 0.6 : 1 }}
                                          >
                                            {isUpdating ? '...' : 'Archive'}
                                          </button>
                                        </>
                                      )}
                                    </div>
                                  </div>
                                );
                              })}
                            </div>
                          )}

                          {/* Archived Versions Section */}
                          {archivedVersions.length > 0 && (
                            <div style={styles.archivedSection}>
                              <button
                                onClick={() => toggleShowArchived(templateType)}
                                style={styles.viewArchivedButton}
                              >
                                {showArchivedForType ? '▼' : '▶'} View archived ({archivedVersions.length})
                              </button>

                              {showArchivedForType && (
                                <div style={styles.archivedVersionsList}>
                                  {archivedVersions.map((version) => (
                                    <div key={version.id} style={styles.archivedVersionItem}>
                                      <div style={styles.versionInfo}>
                                        <span style={styles.archivedVersionLabel}>
                                          {getVersionName(version)}
                                        </span>
                                        <span style={styles.archivedBadge}>Archived</span>
                                      </div>
                                      <button
                                        onClick={() => handleUnarchiveVersion(template.id, version.id)}
                                        disabled={updatingVersion === version.id}
                                        style={{ ...styles.unarchiveButton, opacity: updatingVersion === version.id ? 0.6 : 1 }}
                                      >
                                        {updatingVersion === version.id ? '...' : 'Unarchive'}
                                      </button>
                                    </div>
                                  ))}
                                </div>
                              )}
                            </div>
                          )}

                          {/* Create Version Button */}
                          <button
                            onClick={() => handleCreateVersion(template.id)}
                            disabled={creatingVersions[template.id]}
                            style={{
                              ...styles.createVersionButton,
                              opacity: creatingVersions[template.id] ? 0.6 : 1,
                            }}
                          >
                            {creatingVersions[template.id] ? 'Creating...' : '+ Create Version'}
                          </button>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Create Template Modal */}
      {createTemplateOpen && (
        <div style={styles.modalOverlay} onClick={() => setCreateTemplateOpen(false)}>
          <div style={styles.modalContent} onClick={(e) => e.stopPropagation()}>
            <div style={styles.modalHeader}>
              <h3 style={styles.modalTitle}>Create New Template</h3>
            </div>

            <div style={styles.modalBody}>
              <div style={styles.formGroup}>
                <label style={styles.label}>Template Type *</label>
                <select
                  value={newTemplateType}
                  onChange={(e) => setNewTemplateType(e.target.value)}
                  style={styles.input}
                  disabled={creatingTemplate}
                  required
                >
                  {TEMPLATE_TYPES.map(type => (
                    <option key={type.value} value={type.value}>
                      {type.label}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div style={styles.modalFooter}>
              <button
                onClick={() => setCreateTemplateOpen(false)}
                disabled={creatingTemplate}
                style={styles.cancelButton}
              >
                Cancel
              </button>
              <button
                onClick={handleCreateTemplate}
                disabled={creatingTemplate}
                style={{
                  ...styles.createButton,
                  opacity: creatingTemplate ? 0.6 : 1,
                }}
              >
                {creatingTemplate ? 'Creating...' : 'Create'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

const styles = {
  card: {
    backgroundColor: COLORS.BG_SURFACE_PRIMARY,
    borderRadius: RADIUS.DEFAULT,
    border: `1px solid ${COLORS.BORDER_LIGHT}`,
    boxShadow: SHADOWS.DEFAULT,
    overflow: 'hidden',
    marginTop: SPACING.XL,
  },

  cardHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: SPACING.LG,
    borderBottom: `1px solid ${COLORS.BORDER_LIGHT}`,
    backgroundColor: COLORS.BG_SURFACE_SECONDARY,
  },

  cardTitle: {
    ...TYPOGRAPHY.H3,
    color: COLORS.TEXT_PRIMARY,
    margin: 0,
  },

  createButton: {
    padding: `${SPACING.SM} ${SPACING.LG}`,
    backgroundColor: COLORS.PRIMARY_DARK,
    color: COLORS.TEXT_WHITE,
    border: 'none',
    borderRadius: RADIUS.DEFAULT,
    fontSize: TYPOGRAPHY.BUTTON.fontSize,
    fontWeight: TYPOGRAPHY.BUTTON.fontWeight,
    fontFamily: TYPOGRAPHY.FONT_FAMILY_BODY,
    cursor: 'pointer',
    transition: 'all 0.2s ease',
  },

  errorMessage: {
    margin: SPACING.LG,
    padding: SPACING.MD,
    backgroundColor: COLORS.ERROR_BG,
    border: `1px solid ${COLORS.ERROR}`,
    borderRadius: RADIUS.DEFAULT,
    color: COLORS.ERROR,
    fontSize: TYPOGRAPHY.BODY_SM.fontSize,
    fontWeight: '500',
  },

  emptyState: {
    padding: SPACING.XXXL,
    textAlign: 'center',
    color: COLORS.TEXT_LIGHT,
  },

  secondaryButton: {
    padding: `${SPACING.SM} ${SPACING.LG}`,
    backgroundColor: COLORS.BG_SURFACE_SECONDARY,
    color: COLORS.TEXT_PRIMARY,
    border: `1px solid ${COLORS.BORDER_LIGHT}`,
    borderRadius: RADIUS.DEFAULT,
    fontSize: TYPOGRAPHY.BUTTON.fontSize,
    fontWeight: TYPOGRAPHY.BUTTON.fontWeight,
    fontFamily: TYPOGRAPHY.FONT_FAMILY_BODY,
    cursor: 'pointer',
    transition: 'all 0.2s ease',
  },

  templatesList: {
    padding: SPACING.LG,
  },

  typeSection: {
    marginBottom: SPACING.LG,
    border: `1px solid ${COLORS.BORDER_LIGHT}`,
    borderRadius: RADIUS.DEFAULT,
    overflow: 'hidden',
  },

  typeHeader: {
    width: '100%',
    padding: SPACING.LG,
    border: 'none',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    transition: 'background-color 0.2s ease',
  },

  typeTitle: {
    ...TYPOGRAPHY.H4,
    color: COLORS.TEXT_PRIMARY,
    margin: 0,
    fontWeight: '600',
  },

  typeCount: {
    ...TYPOGRAPHY.BODY_SM,
    color: COLORS.TEXT_LIGHT,
    marginLeft: SPACING.SM,
  },

  typeContent: {
    borderTop: `1px solid ${COLORS.BORDER_LIGHT}`,
    paddingBottom: 0,
  },

  templateItem: {
    padding: SPACING.LG,
    borderBottom: `1px solid ${COLORS.BORDER_LIGHT}`,
  },

  templateHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: SPACING.MD,
  },

  templateInfo: {
    display: 'flex',
    alignItems: 'center',
    gap: SPACING.MD,
  },

  templateName: {
    ...TYPOGRAPHY.H4,
    color: COLORS.TEXT_PRIMARY,
    margin: 0,
  },

  versionsList: {
    marginBottom: SPACING.MD,
    paddingLeft: SPACING.LG,
    borderLeft: `2px solid ${COLORS.BORDER_LIGHT}`,
  },

  versionItem: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingBottom: SPACING.SM,
    marginBottom: SPACING.SM,
  },

  versionInfo: {
    display: 'flex',
    alignItems: 'center',
    gap: SPACING.MD,
    flex: 1,
  },

  versionLabel: {
    ...TYPOGRAPHY.BODY_SM,
    color: COLORS.TEXT_PRIMARY,
    fontWeight: '600',
  },

  versionNameInput: {
    padding: `${SPACING.XS} ${SPACING.SM}`,
    border: `1px solid ${COLORS.BORDER_DEFAULT}`,
    borderRadius: RADIUS.DEFAULT,
    fontSize: TYPOGRAPHY.BODY_SM.fontSize,
    fontFamily: TYPOGRAPHY.FONT_FAMILY_BODY,
    color: COLORS.TEXT_PRIMARY,
    backgroundColor: COLORS.BG_WHITE,
    outline: 'none',
    flex: 0.8,
  },

  activeBadge: {
    ...TYPOGRAPHY.BODY_SM,
    backgroundColor: COLORS.STATUS_FUNDED_BG,
    color: COLORS.STATUS_FUNDED_TEXT,
    padding: `${SPACING.XS} ${SPACING.SM}`,
    borderRadius: RADIUS.FULL,
    fontWeight: '600',
  },

  archivedBadge: {
    ...TYPOGRAPHY.BODY_SM,
    backgroundColor: COLORS.BG_SURFACE_SECONDARY,
    color: COLORS.TEXT_LIGHT,
    padding: `${SPACING.XS} ${SPACING.SM}`,
    borderRadius: RADIUS.FULL,
    fontWeight: '600',
  },

  versionActions: {
    display: 'flex',
    gap: SPACING.SM,
  },

  editButton: {
    padding: `${SPACING.XS} ${SPACING.MD}`,
    backgroundColor: COLORS.PRIMARY_DARK,
    color: COLORS.TEXT_WHITE,
    border: 'none',
    borderRadius: RADIUS.DEFAULT,
    fontSize: '12px',
    fontWeight: '600',
    fontFamily: TYPOGRAPHY.FONT_FAMILY_BODY,
    cursor: 'pointer',
    transition: 'all 0.2s ease',
  },

  archiveButton: {
    padding: `${SPACING.XS} ${SPACING.MD}`,
    backgroundColor: COLORS.BG_SURFACE_SECONDARY,
    color: COLORS.TEXT_LIGHT,
    border: `1px solid ${COLORS.BORDER_LIGHT}`,
    borderRadius: RADIUS.DEFAULT,
    fontSize: '12px',
    fontWeight: '600',
    fontFamily: TYPOGRAPHY.FONT_FAMILY_BODY,
    cursor: 'pointer',
    transition: 'all 0.2s ease',
  },

  unarchiveButton: {
    padding: `${SPACING.XS} ${SPACING.MD}`,
    backgroundColor: COLORS.BG_SURFACE_SECONDARY,
    color: COLORS.TEXT_PRIMARY,
    border: `1px solid ${COLORS.BORDER_LIGHT}`,
    borderRadius: RADIUS.DEFAULT,
    fontSize: '12px',
    fontWeight: '600',
    fontFamily: TYPOGRAPHY.FONT_FAMILY_BODY,
    cursor: 'pointer',
    transition: 'all 0.2s ease',
  },

  archivedSection: {
    paddingTop: SPACING.MD,
    borderTop: `1px dashed ${COLORS.BORDER_LIGHT}`,
  },

  viewArchivedButton: {
    padding: `${SPACING.XS} ${SPACING.SM}`,
    backgroundColor: 'transparent',
    color: COLORS.TEXT_LIGHT,
    border: 'none',
    borderRadius: RADIUS.DEFAULT,
    fontSize: TYPOGRAPHY.BODY_SM.fontSize,
    fontWeight: '600',
    fontFamily: TYPOGRAPHY.FONT_FAMILY_BODY,
    cursor: 'pointer',
    transition: 'color 0.2s ease',
  },

  archivedVersionsList: {
    marginTop: SPACING.SM,
    paddingLeft: SPACING.LG,
    borderLeft: `2px solid ${COLORS.BORDER_LIGHT}`,
  },

  archivedVersionItem: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingBottom: SPACING.SM,
    marginBottom: SPACING.SM,
    opacity: 0.7,
  },

  archivedVersionLabel: {
    ...TYPOGRAPHY.BODY_SM,
    color: COLORS.TEXT_LIGHT,
    fontWeight: '500',
    textDecoration: 'line-through',
  },

  createVersionButton: {
    width: '100%',
    padding: `${SPACING.SM} ${SPACING.MD}`,
    backgroundColor: COLORS.BG_SURFACE_SECONDARY,
    color: COLORS.TEXT_PRIMARY,
    border: `1px dashed ${COLORS.BORDER_DEFAULT}`,
    borderRadius: RADIUS.DEFAULT,
    fontSize: TYPOGRAPHY.BODY_SM.fontSize,
    fontWeight: '600',
    fontFamily: TYPOGRAPHY.FONT_FAMILY_BODY,
    cursor: 'pointer',
    transition: 'all 0.2s ease',
  },

  modalOverlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 1000,
  },

  modalContent: {
    backgroundColor: COLORS.BG_SURFACE_PRIMARY,
    borderRadius: RADIUS.DEFAULT,
    boxShadow: SHADOWS.LG,
    maxWidth: '500px',
    width: '90%',
    maxHeight: '90vh',
    overflow: 'auto',
  },

  modalHeader: {
    padding: SPACING.LG,
    borderBottom: `1px solid ${COLORS.BORDER_LIGHT}`,
    backgroundColor: COLORS.BG_SURFACE_SECONDARY,
  },

  modalTitle: {
    ...TYPOGRAPHY.H3,
    color: COLORS.TEXT_PRIMARY,
    margin: 0,
  },

  modalBody: {
    padding: SPACING.LG,
  },

  modalFooter: {
    padding: SPACING.LG,
    borderTop: `1px solid ${COLORS.BORDER_LIGHT}`,
    backgroundColor: COLORS.BG_SURFACE_SECONDARY,
    display: 'flex',
    gap: SPACING.MD,
    justifyContent: 'flex-end',
  },

  formGroup: {
    marginBottom: SPACING.LG,
  },

  label: {
    display: 'block',
    ...TYPOGRAPHY.LABEL,
    color: COLORS.TEXT_PRIMARY,
    marginBottom: SPACING.SM,
    fontWeight: '600',
  },

  input: {
    width: '100%',
    padding: `${SPACING.SM} ${SPACING.MD}`,
    border: `1px solid ${COLORS.BORDER_LIGHT}`,
    borderRadius: RADIUS.DEFAULT,
    fontSize: TYPOGRAPHY.BODY.fontSize,
    fontFamily: TYPOGRAPHY.FONT_FAMILY_BODY,
    color: COLORS.TEXT_PRIMARY,
    backgroundColor: COLORS.BG_WHITE,
    outline: 'none',
    transition: 'border-color 0.2s ease, box-shadow 0.2s ease',
    boxSizing: 'border-box',
  },

  cancelButton: {
    padding: `${SPACING.SM} ${SPACING.LG}`,
    backgroundColor: COLORS.BG_SURFACE_PRIMARY,
    color: COLORS.TEXT_PRIMARY,
    border: `1px solid ${COLORS.BORDER_LIGHT}`,
    borderRadius: RADIUS.DEFAULT,
    fontSize: TYPOGRAPHY.BUTTON.fontSize,
    fontWeight: TYPOGRAPHY.BUTTON.fontWeight,
    fontFamily: TYPOGRAPHY.FONT_FAMILY_BODY,
    cursor: 'pointer',
    transition: 'all 0.2s ease',
  },
};
